/**
 * Phase → frame mapping and loop timing for N1 clips. Pure functions, unit-tested.
 *
 * - Transitions are driven by a normalized phase 0..1 (time, or the jump's ascent/descent
 *   physics phase). `phaseToFrame` always hits frame 0 at phase 0 and frame n−1 at phase 1, so
 *   the manifest's contact frames are shown exactly at the chain boundaries.
 * - Loops are driven by wall-clock: `loopFrame` wraps a cycle of `frames` over `cycleMs`.
 */

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Guards floor() against phase × frames landing a rounding error below an integer. */
const EPSILON = 1e-9;

/** Frame index for a phase, over frames startFrame..frames−1 (both ends hit exactly). */
export function phaseToFrame(phase: number, frames: number, startFrame = 0): number {
  const span = frames - startFrame;
  if (span <= 1) return Math.max(0, frames - 1);
  return startFrame + Math.min(span - 1, Math.floor(clamp01(phase) * span + EPSILON));
}

/** Wall-clock loop frame: elapsed wraps over cycleMs, mapped to frames 0..frames−1. */
export function loopFrame(elapsedMs: number, cycleMs: number, frames: number): number {
  if (!(cycleMs > 0) || frames <= 1) return 0;
  const t = ((elapsedMs % cycleMs) + cycleMs) % cycleMs;
  return Math.min(frames - 1, Math.floor((t / cycleMs) * frames + EPSILON));
}

/**
 * Ascent phase from the vertical velocity (Phaser sign: +y down, so vy < 0 while rising).
 * 0 at takeoff (vy = −jumpVelocity), 1 at the apex (vy = 0) — i.e. 1 − |vy| / vy0.
 */
export function ascentPhase(vy: number, jumpVelocity: number): number {
  return clamp01(1 + vy / jumpVelocity);
}

/**
 * Descent phase from the vertical velocity: 0 at the apex, 1 when falling as fast as the takeoff
 * speed — exactly the flat-ground landing of a symmetric arc. Longer falls hold the last frame.
 */
export function descentPhase(vy: number, jumpVelocity: number): number {
  return clamp01(vy / jumpVelocity);
}

export interface FramePhase {
  frame: number;
  /** Position between `frame` and `next` (0 ≤ fraction < 1). */
  fraction: number;
  /** The frame to blend toward (equals `frame` at the last frame of a one-shot). */
  next: number;
}

/** Sub-frame position of a phase over frames startFrame..frames−1 (one-shots hold their last frame). */
export function phaseToFramePhase(phase: number, frames: number, startFrame = 0): FramePhase {
  const span = frames - startFrame;
  if (span <= 1)
    return { frame: Math.max(0, frames - 1), fraction: 0, next: Math.max(0, frames - 1) };
  const raw = clamp01(phase) * span;
  const local = Math.min(span - 1, Math.floor(raw + EPSILON));
  const frame = startFrame + local;
  const fraction = local >= span - 1 ? 0 : Math.min(0.999, Math.max(0, raw - local));
  return { frame, fraction, next: Math.min(frames - 1, frame + 1) };
}

/** Sub-frame position of a wall-clock loop (wraps: the last frame blends toward frame 0). */
export function loopFramePhase(elapsedMs: number, cycleMs: number, frames: number): FramePhase {
  if (!(cycleMs > 0) || frames <= 1) return { frame: 0, fraction: 0, next: 0 };
  const t = ((elapsedMs % cycleMs) + cycleMs) % cycleMs;
  const raw = (t / cycleMs) * frames;
  const frame = Math.min(frames - 1, Math.floor(raw + EPSILON));
  return {
    frame,
    fraction: Math.min(0.999, Math.max(0, raw - frame)),
    next: (frame + 1) % frames,
  };
}
